import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import apiClient from '../services/api';
import {
  formatDateWith,
  formatTimeWith,
  formatDateTimeWith,
  formatCurrencyWith,
} from '../utils/dateFormat';

const AppSettingsContext = createContext(null);

const DEFAULT_SETTINGS = {
  timezone: 'UTC',
  date_format: 'DD/MM/YYYY',
  time_format: 'HH:mm',
  default_country: null,
  default_currency: null,
  default_language: null,
  order_menu_fields: {
    image: true, description: true, category: true, price: true,
    veg: false, spicy_level: false, prep_time: false, cook_time: false,
    serving_vessel: false, cooking_station: false, plating_note: false, ingredients: false,
  },
  date_format_choices: [],
  time_format_choices: [],
};

export function AppSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/users/settings/app/');
      if (data?.success && data.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...data.data });
      }
    } catch (err) {
      // The settings endpoint may not be reachable yet, so fall back to defaults and keep the app usable.
      console.warn('AppSettings: unable to load, using defaults', err);
    } finally {
      setLoaded(true);
    }
  }, []);

  const update = useCallback(async (patch) => {
    const { data } = await apiClient.put('/users/settings/app/', patch);
    if (data?.success && data.data) {
      setSettings({ ...DEFAULT_SETTINGS, ...data.data });
    }
    return data;
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => {
    const tz = settings.timezone;
    const df = settings.date_format;
    const tf = settings.time_format;
    return {
      settings,
      loaded,
      refresh,
      update,
      formatDate: (d) => formatDateWith(d, df, tz),
      formatTime: (d) => formatTimeWith(d, tf, tz),
      formatDateTime: (d) => formatDateTimeWith(d, df, tf, tz),
      formatCurrency: (n) => formatCurrencyWith(n, settings.default_currency),
    };
  }, [settings, loaded, refresh, update]);

  return <AppSettingsContext.Provider value={value}>{children}</AppSettingsContext.Provider>;
}

export function useAppSettings() {
  const ctx = useContext(AppSettingsContext);
  if (!ctx) {
    // Permissive fallback so components rendering outside the provider still render without crashing.
    return {
      settings: DEFAULT_SETTINGS,
      loaded: false,
      refresh: async () => {},
      update: async () => {},
      formatDate: (d) => formatDateWith(d, DEFAULT_SETTINGS.date_format, DEFAULT_SETTINGS.timezone),
      formatTime: (d) => formatTimeWith(d, DEFAULT_SETTINGS.time_format, DEFAULT_SETTINGS.timezone),
      formatDateTime: (d) => formatDateTimeWith(d, DEFAULT_SETTINGS.date_format, DEFAULT_SETTINGS.time_format, DEFAULT_SETTINGS.timezone),
      formatCurrency: (n) => formatCurrencyWith(n, null),
    };
  }
  return ctx;
}
